import XLSX from 'xlsx-js-style';

const COLUMNAS = [
    { header: 'Folio', key: 'id', width: 8 },
    { header: 'Fecha', key: 'fecha', width: 12 },
    { header: 'Puesto', key: 'puesto', width: 26 },
    { header: 'Tipo', key: 'tipo', width: 26 },
    { header: 'Confidencial', key: 'confidencial', width: 13 },
    { header: 'Zona', key: 'zona', width: 18 },
    { header: 'Cliente', key: 'cliente', width: 26 },
    { header: 'Solicitante', key: 'solicitante', width: 26 },
    { header: 'Reemplaza a', key: 'reemplaza', width: 26 },
    { header: 'Cubierta por', key: 'cubre', width: 26 },
    { header: 'Estatus', key: 'estatus', width: 14 },
    { header: 'Conclusión', key: 'fechaConclusion', width: 13 },
    { header: 'Descripción', key: 'descripcion', width: 50 },
];

const COLOR = {
    primario: '1F3A5F',
    texto: '1F2937',
    suave: '6B7280',
    borde: 'D9DEE5',
    zebra: 'F5F7FA',
    confidencial: 'B45309',
};

const ESTATUS_ESTILO = {
    ABIERTA: { fill: 'DCEBFB', font: '1D4E89' },
    'EN PROCESO': { fill: 'FFF1D6', font: '8A5A00' },
    CUBIERTA: { fill: 'DDF3E4', font: '1E6B3A' },
    CANCELADA: { fill: 'FBE0E0', font: '9B2C2C' },
};

const LINEA = { style: 'thin', color: { rgb: COLOR.borde } };
const BORDES = { top: LINEA, bottom: LINEA, left: LINEA, right: LINEA };
const FUENTE = { name: 'Calibri', sz: 11, color: { rgb: COLOR.texto } };

const ESTILO_TITULO = { font: { ...FUENTE, sz: 16, bold: true, color: { rgb: COLOR.primario } } };
const ESTILO_SUBTITULO = { font: { ...FUENTE, sz: 10, color: { rgb: COLOR.suave } } };
const ESTILO_ENCABEZADO = {
    font: { ...FUENTE, bold: true, color: { rgb: 'FFFFFF' } },
    fill: { fgColor: { rgb: COLOR.primario } },
    alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
    border: BORDES,
};

const FILA_ENCABEZADO = 4;

const celda = (r, c) => XLSX.utils.encode_cell({ r, c });

// Fecha como número de Excel (sin hora) para que se pueda ordenar y filtrar
const COLUMNAS_FECHA = ['fecha', 'fechaConclusion'];

const fechaExcel = (fecha) => {
    if (!fecha) return '';
    // YYYY-MM-DD se toma como fecha local para no recorrer un día
    const d = fecha.length === 10 ? new Date(`${fecha}T00:00:00`) : new Date(fecha);
    return (Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()) - Date.UTC(1899, 11, 30)) / 86400000;
};

const valorCelda = (vacante, key) => {
    if (COLUMNAS_FECHA.includes(key)) return fechaExcel(vacante[key]);
    if (key === 'confidencial') return vacante.confidencial ? 'Sí' : 'No';
    return vacante[key] ?? '';
};

const estiloEstatus = (estatus) => {
    const color = ESTATUS_ESTILO[estatus];
    if (!color) return {};
    return {
        fill: { fgColor: { rgb: color.fill } },
        font: { ...FUENTE, bold: true, color: { rgb: color.font } },
        alignment: { horizontal: 'center', vertical: 'top' },
    };
};

const estiloDato = (vacante, key, zebra) => {
    const base = {
        font: FUENTE,
        border: BORDES,
        alignment: { vertical: 'top', wrapText: key === 'descripcion' },
        ...(zebra && { fill: { fgColor: { rgb: COLOR.zebra } } }),
    };

    if (key === 'estatus') return { ...base, ...estiloEstatus(vacante.estatus) };
    if (key === 'id' || COLUMNAS_FECHA.includes(key)) return { ...base, alignment: { horizontal: 'center', vertical: 'top' } };
    if (key === 'confidencial') {
        return {
            ...base,
            alignment: { horizontal: 'center', vertical: 'top' },
            font: vacante.confidencial ? { ...FUENTE, bold: true, color: { rgb: COLOR.confidencial } } : FUENTE,
        };
    }
    return base;
};

const crearHojaVacantes = (vacantes, filtrosActivos) => {
    const ultimaCol = COLUMNAS.length - 1;
    const ultimaFila = FILA_ENCABEZADO + vacantes.length;

    const filas = [
        ['Reporte de vacantes'],
        [`Generado el ${new Date().toLocaleString('es-MX')}   |   ${vacantes.length} vacantes`],
        [filtrosActivos.length ? `Filtros: ${filtrosActivos.join(', ')}` : 'Sin filtros aplicados'],
        [],
        COLUMNAS.map((c) => c.header),
        ...vacantes.map((v) => COLUMNAS.map((c) => valorCelda(v, c.key))),
    ];

    const ws = XLSX.utils.aoa_to_sheet(filas);

    ws[celda(0, 0)].s = ESTILO_TITULO;
    ws[celda(1, 0)].s = ESTILO_SUBTITULO;
    ws[celda(2, 0)].s = ESTILO_SUBTITULO;

    COLUMNAS.forEach((col, c) => {
        ws[celda(FILA_ENCABEZADO, c)].s = ESTILO_ENCABEZADO;

        vacantes.forEach((vacante, i) => {
            const ref = celda(FILA_ENCABEZADO + 1 + i, c);
            ws[ref].s = estiloDato(vacante, col.key, i % 2 === 1);
            if (COLUMNAS_FECHA.includes(col.key) && ws[ref].v !== '') ws[ref].z = 'dd/mm/yyyy';
        });
    });

    ws['!merges'] = [0, 1, 2].map((r) => ({ s: { r, c: 0 }, e: { r, c: ultimaCol } }));
    ws['!cols'] = COLUMNAS.map((c) => ({ wch: c.width }));
    ws['!rows'] = [{ hpt: 26 }, { hpt: 16 }, { hpt: 16 }, { hpt: 8 }, { hpt: 24 }];
    if (vacantes.length) {
        ws['!autofilter'] = { ref: `${celda(FILA_ENCABEZADO, 0)}:${celda(ultimaFila, ultimaCol)}` };
    }

    return ws;
};

const contarPor = (vacantes, key) => {
    const conteo = {};
    vacantes.forEach((v) => {
        const nombre = v[key] ?? 'Sin asignar';
        conteo[nombre] = (conteo[nombre] ?? 0) + 1;
    });
    return Object.entries(conteo).sort((a, b) => b[1] - a[1]);
};

const crearHojaResumen = (vacantes) => {
    const filas = [['Resumen de vacantes'], []];
    const encabezados = [];
    const totales = [];

    const agregarTabla = (titulo, key) => {
        encabezados.push(filas.length);
        filas.push([titulo, 'Vacantes']);
        contarPor(vacantes, key).forEach((fila) => filas.push(fila));
        totales.push(filas.length);
        filas.push(['Total', vacantes.length]);
        filas.push([]);
    };

    agregarTabla('Estatus', 'estatus');
    agregarTabla('Tipo', 'tipo');
    agregarTabla('Cliente', 'cliente');

    const ws = XLSX.utils.aoa_to_sheet(filas);
    ws[celda(0, 0)].s = ESTILO_TITULO;

    filas.forEach((fila, r) => {
        if (r < 2 || !fila.length) return;

        [0, 1].forEach((c) => {
            const ref = celda(r, c);
            const alineacion = { horizontal: c === 1 ? 'center' : 'left', vertical: 'center', indent: c === 0 ? 1 : 0 };

            if (encabezados.includes(r)) {
                ws[ref].s = { ...ESTILO_ENCABEZADO, alignment: alineacion };
            } else if (totales.includes(r)) {
                ws[ref].s = {
                    font: { ...FUENTE, bold: true },
                    fill: { fgColor: { rgb: COLOR.zebra } },
                    border: { ...BORDES, top: { style: 'medium', color: { rgb: COLOR.primario } } },
                    alignment: alineacion,
                };
            } else {
                const estatus = c === 0 ? estiloEstatus(fila[0]) : {};
                ws[ref].s = { font: FUENTE, border: BORDES, ...estatus, alignment: alineacion };
            }
        });
    });

    ws['!cols'] = [{ wch: 32 }, { wch: 12 }];
    ws['!rows'] = [{ hpt: 26 }];

    return ws;
};

export const exportarVacantesExcel = (vacantes, filtrosActivos = []) => {
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, crearHojaVacantes(vacantes, filtrosActivos), 'Vacantes');
    XLSX.utils.book_append_sheet(wb, crearHojaResumen(vacantes), 'Resumen');

    const hoy = new Date().toLocaleDateString('en-CA'); // YYYY-MM-DD
    XLSX.writeFile(wb, `Vacantes_${hoy}.xlsx`);
};